import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { formatSgd, formatNum, apiErr } from "../../lib/utils";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input, Label } from "../ui/input";
import { toast } from "sonner";
import {
  ArrowLeft, Calculator, RefreshCw, Save, ShieldAlert, Landmark, TrendingUp, Layers,
} from "lucide-react";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ReferenceLine, ResponsiveContainer, CartesianGrid,
} from "recharts";
import CapitalStackOptimiser from "./CapitalStackOptimiser";

const CAPEX_FIELDS = [["equipment_cost", "Equipment"], ["implementation_cost", "Implementation"],
  ["consultancy_cost", "Consultancy"], ["software_cost", "Software"]];
const SAVINGS_FIELDS = [["energy_savings", "Energy"], ["fuel_savings", "Fuel"], ["water_savings", "Water"],
  ["waste_savings", "Waste"], ["labour_savings", "Labour"], ["carbon_cost_savings", "Carbon cost"],
  ["revenue_uplift", "Revenue uplift"], ["insurance_benefit", "Insurance / resilience"]];

const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
const fmtPayback = (v) => (v == null ? "—" : `${v} yrs`);
const fmtPct = (v) => (v == null ? "—" : `${v}%`);

function NumField({ label, k, f, set, testid, suffix }) {
  return (
    <div>
      <Label className="text-[10px]">{label}{suffix ? ` (${suffix})` : ""}</Label>
      <Input type="number" value={f[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={testid || `sf-case-input-${k}`} />
    </div>
  );
}

function Kpi({ label, value, testid }) {
  return (
    <div className="border border-border rounded-sm p-3 text-center" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-mono text-base font-bold mt-1">{value}</div>
    </div>
  );
}

export default function InvestmentCaseBuilder({ id, onBack }) {
  const [data, setData] = useState(null);
  const [f, setF] = useState(null);
  const [scens, setScens] = useState(null);
  const [sel, setSel] = useState("base");
  const [view, setView] = useState("unlevered");
  const [busy, setBusy] = useState(false);
  const [showOpt, setShowOpt] = useState(false);

  const reload = () => api.get(`/susfin/case/${id}`).then((r) => {
    setData(r.data); setF(r.data.inputs); setScens(r.data.scenarios);
  }).catch((e) => toast.error(apiErr(e)));

  useEffect(() => {
    api.get(`/susfin/case/${id}`).then((r) => {
      setData(r.data); setF(r.data.inputs); setScens(r.data.scenarios);
    }).catch((e) => toast.error(apiErr(e)));
  }, [id]);

  if (!data || !f) return <div className="p-8 text-muted-foreground font-mono text-sm">Loading case builder…</div>;

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setScen = (name, k, v) => setScens((p) => p.map((s) => (s.name === name ? { ...s, [k]: v } : s)));

  const body = (save) => ({
    inputs: Object.fromEntries(Object.entries(f).map(([k, v]) => [k, k === "avoided_loss_enabled" ? !!v : num(v)])),
    scenarios: scens.map((s) => ({ ...s, savings_pct: Number(s.savings_pct), capex_pct: Number(s.capex_pct), rate_delta: Number(s.rate_delta) })),
    save,
  });
  const run = async (save) => {
    setBusy(true);
    try {
      const r = await api.put(`/susfin/case/${id}`, body(save));
      setData(r.data);
      if (save) toast.success("Investment case saved — register figures updated");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const r = data.results.find((x) => x.name === sel) || data.results[0];
  const m = r[view];
  const t = r.totals;
  const chartData = r.cashflow.map((c) => ({
    year: `Y${c.year}`, net: view === "unlevered" ? c.unlevered : c.levered,
    cumulative: view === "unlevered" ? c.cum_unlevered : c.cum_levered,
  }));

  return (
    <div className="space-y-5" data-testid="sf-case-builder">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <button onClick={onBack} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-1" data-testid="sf-case-back">
            <ArrowLeft className="h-4 w-4" /> Investment Register
          </button>
          <h2 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2">
            <Calculator className="h-5 w-5 text-accent" /> Investment Case — {data.initiative_name}
            <Badge variant="outline" className="text-[9px] uppercase" data-testid="sf-case-stage">{data.pipeline_stage.replace(/_/g, " ")}</Badge>
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setShowOpt(true)} data-testid="sf-case-optimiser">
            <Layers className="h-4 w-4" /> Optimiser
          </Button>
          <Button variant="outline" onClick={() => run(false)} disabled={busy} data-testid="sf-case-recalc">
            <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} /> Recalculate
          </Button>
          <Button variant="primary" onClick={() => run(true)} disabled={busy} data-testid="sf-case-save">
            <Save className="h-4 w-4" /> Save case
          </Button>
        </div>
      </div>

      <CapitalStackOptimiser iid={id} open={showOpt} onClose={() => setShowOpt(false)} onApplied={reload} />

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-5">
        {/* ------------ inputs ------------ */}
        <div className="xl:col-span-2 space-y-4">
          <div className="card-flat p-4">
            <div className="label-eyebrow mb-3">CAPEX breakdown (S$)</div>
            <div className="grid grid-cols-2 gap-3">
              {CAPEX_FIELDS.map(([k, l]) => <NumField key={k} label={l} k={k} f={f} set={set} />)}
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <NumField label="Annual maintenance / OPEX" k="annual_opex" f={f} set={set} suffix="S$/yr" />
              <NumField label="Useful life" k="useful_life_years" f={f} set={set} suffix="years" />
            </div>
          </div>

          <div className="card-flat p-4">
            <div className="label-eyebrow mb-3">Annual savings & benefits (S$/yr)</div>
            <div className="grid grid-cols-2 gap-3">
              {SAVINGS_FIELDS.map(([k, l]) => <NumField key={k} label={l} k={k} f={f} set={set} />)}
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <NumField label="Carbon reduction" k="annual_tco2e_reduction" f={f} set={set} suffix="tCO₂e/yr" />
            </div>
          </div>

          <div className="card-flat p-4" data-testid="sf-case-avoided-block">
            <div className="flex items-center justify-between mb-3">
              <div className="label-eyebrow inline-flex items-center gap-1"><ShieldAlert className="h-3.5 w-3.5 text-[#FFB800]" /> Avoided-loss economics (adaptation)</div>
              <button type="button" onClick={() => set("avoided_loss_enabled", !f.avoided_loss_enabled)} data-testid="sf-case-avoided-toggle"
                className={`px-2.5 py-1 rounded-sm border text-xs transition-colors ${f.avoided_loss_enabled ? "border-accent bg-accent/10" : "border-border text-muted-foreground hover:bg-surface-hover"}`}>
                {f.avoided_loss_enabled ? "Enabled" : "Off"}
              </button>
            </div>
            {f.avoided_loss_enabled ? (
              <>
                <div className="grid grid-cols-3 gap-3">
                  <NumField label="P(disruption) before" k="prob_before_pct" f={f} set={set} suffix="%/yr" />
                  <NumField label="P(disruption) after" k="prob_after_pct" f={f} set={set} suffix="%/yr" />
                  <NumField label="Loss per event" k="loss_per_event_sgd" f={f} set={set} suffix="S$" />
                </div>
                {t.avoided_loss > 0 && (
                  <div className="grid grid-cols-3 gap-2 mt-3 text-center" data-testid="sf-case-eal">
                    <div className="border border-border rounded-sm p-2"><div className="text-[9px] uppercase text-muted-foreground">EAL before</div><div className="font-mono text-sm font-bold text-red-400">{formatSgd(t.eal_before)}</div></div>
                    <div className="border border-border rounded-sm p-2"><div className="text-[9px] uppercase text-muted-foreground">EAL after</div><div className="font-mono text-sm font-bold">{formatSgd(t.eal_after)}</div></div>
                    <div className="border border-accent/50 rounded-sm p-2"><div className="text-[9px] uppercase text-muted-foreground">Avoided loss / yr</div><div className="font-mono text-sm font-bold text-accent">{formatSgd(t.avoided_loss)}</div></div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-[11px] text-muted-foreground">Enable to value resilience: expected annual loss before vs after the investment counts as a benefit line.</p>
            )}
          </div>

          <div className="card-flat p-4">
            <div className="label-eyebrow mb-3 inline-flex items-center gap-1"><Landmark className="h-3.5 w-3.5 text-accent" /> Grant & debt modelling</div>
            <div className="grid grid-cols-2 gap-3">
              <NumField label="Potential grant" k="grant_amount" f={f} set={set} suffix="S$" />
              <NumField label="Debt requirement" k="debt_amount" f={f} set={set} suffix="S$" />
              <NumField label="Interest rate" k="interest_rate" f={f} set={set} suffix="%/yr" />
              <NumField label="Tenure" k="tenure_months" f={f} set={set} suffix="months" />
              <NumField label="Discount rate" k="discount_rate" f={f} set={set} suffix="%" />
            </div>
            {r.debt_note && <p className="text-[11px] text-[#FFB800] mt-2" data-testid="sf-case-debt-note">{r.debt_note}</p>}
          </div>
        </div>

        {/* ------------ results ------------ */}
        <div className="xl:col-span-3 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-1.5 flex-wrap">
              {data.results.map((s) => (
                <Button key={s.name} size="sm" variant={sel === s.name ? "primary" : "outline"} onClick={() => setSel(s.name)} data-testid={`sf-case-scenario-${s.name}`}>
                  {s.label}
                </Button>
              ))}
            </div>
            <div className="flex items-center border border-border rounded-sm overflow-hidden" data-testid="sf-case-view-toggle">
              {[["unlevered", "Project view"], ["levered", "After debt service"]].map(([v, l]) => (
                <button key={v} onClick={() => setView(v)}
                  className={`px-3 py-1.5 text-xs transition-colors ${view === v ? "bg-accent/15 text-accent" : "text-muted-foreground hover:bg-surface-hover"}`}
                  data-testid={`sf-case-view-${v}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>

          <div className="card-flat p-4">
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-3">
              <Kpi label="NPV" value={m.npv != null ? formatSgd(m.npv) : "—"} testid="sf-case-kpi-npv" />
              <Kpi label="IRR" value={fmtPct(m.irr_pct)} testid="sf-case-kpi-irr" />
              <Kpi label="ROI /yr" value={fmtPct(m.roi_pct)} testid="sf-case-kpi-roi" />
              <Kpi label="Payback" value={fmtPayback(m.simple_payback_years)} testid="sf-case-kpi-payback" />
              <Kpi label="Disc. payback" value={fmtPayback(m.discounted_payback_years)} testid="sf-case-kpi-dpayback" />
              <Kpi label="Cost /tCO₂e" value={r.carbon.cost_per_t_net != null ? formatSgd(r.carbon.cost_per_t_net) : "—"} testid="sf-case-kpi-costpert" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-center text-xs" data-testid="sf-case-stack">
              {[["Total CAPEX", t.total_capex], ["Grant", t.grant], ["Net CAPEX", t.net_capex], ["Debt", t.debt], ["Company cash", t.equity]].map(([l, v]) => (
                <div key={l} className="border border-border rounded-sm p-2">
                  <div className="text-[9px] uppercase text-muted-foreground">{l}</div>
                  <div className="font-mono font-bold mt-0.5">{formatSgd(v)}</div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between text-xs text-muted-foreground mt-3">
              <span>Annual net benefit <span className="font-mono text-foreground font-bold">{formatSgd(t.annual_net_benefit)}</span> · gross {formatSgd(t.annual_gross_benefit)} − OPEX {formatSgd(t.annual_opex)}</span>
              <span>{formatNum(r.carbon.annual_t, { decimals: 1 })} tCO₂e/yr · {formatNum(r.carbon.lifetime_t)} t lifetime</span>
            </div>
          </div>

          <div className="card-flat p-4" data-testid="sf-case-chart">
            <div className="label-eyebrow mb-2 inline-flex items-center gap-1"><TrendingUp className="h-3.5 w-3.5 text-accent" /> Cash flow — {r.label} ({view === "unlevered" ? "project" : "after debt service"})</div>
            <ResponsiveContainer width="100%" height={220}>
              <ComposedChart data={chartData} margin={{ top: 5, right: 10, bottom: 0, left: 10 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis dataKey="year" tick={{ fontSize: 10, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "#9CA3AF" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                <Tooltip contentStyle={{ background: "#16181D", border: "1px solid #2A2D34", fontSize: 12 }} formatter={(v, n) => [formatSgd(v), n === "net" ? "Net cash flow" : "Cumulative"]} />
                <ReferenceLine y={0} stroke="rgba(255,255,255,0.25)" />
                <Bar dataKey="net" fill="#3F4854" radius={[2, 2, 0, 0]} maxBarSize={26} />
                <Line dataKey="cumulative" stroke="#D4FF00" strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div className="card-flat p-4 overflow-x-auto" data-testid="sf-case-compare">
            <div className="label-eyebrow mb-2">Scenario comparison ({view === "unlevered" ? "project view" : "after debt service"})</div>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-muted-foreground text-left border-b border-border">
                  <th className="py-2 pr-2">Scenario</th>
                  <th className="py-2 pr-2">Savings %</th><th className="py-2 pr-2">CAPEX %</th><th className="py-2 pr-2">Rate Δ</th>
                  <th className="py-2 pr-2 text-right">Net CAPEX</th><th className="py-2 pr-2 text-right">Net /yr</th>
                  <th className="py-2 pr-2 text-right">NPV</th><th className="py-2 pr-2 text-right">IRR</th><th className="py-2 text-right">Payback</th>
                </tr>
              </thead>
              <tbody>
                {data.results.map((s) => {
                  const adj = scens.find((x) => x.name === s.name) || s.adjustments;
                  const sm = s[view];
                  return (
                    <tr key={s.name} className={`border-b border-border/50 ${sel === s.name ? "bg-accent/5" : ""}`} data-testid={`sf-case-compare-${s.name}`}>
                      <td className="py-1.5 pr-2 font-medium cursor-pointer" onClick={() => setSel(s.name)}>{s.label}</td>
                      {["savings_pct", "capex_pct", "rate_delta"].map((k) => (
                        <td key={k} className="py-1.5 pr-2">
                          <Input type="number" className="h-7 w-16 text-xs px-1.5" value={adj[k]} disabled={s.name === "base" && k !== "rate_delta" ? false : false}
                            onChange={(e) => setScen(s.name, k, e.target.value)} data-testid={`sf-case-adj-${s.name}-${k}`} />
                        </td>
                      ))}
                      <td className="py-1.5 pr-2 text-right font-mono">{formatSgd(s.totals.net_capex)}</td>
                      <td className="py-1.5 pr-2 text-right font-mono">{formatSgd(s.totals.annual_net_benefit)}</td>
                      <td className={`py-1.5 pr-2 text-right font-mono ${sm.npv >= 0 ? "text-[#00E65B]" : "text-red-400"}`}>{formatSgd(sm.npv)}</td>
                      <td className="py-1.5 pr-2 text-right font-mono">{fmtPct(sm.irr_pct)}</td>
                      <td className="py-1.5 text-right font-mono">{fmtPayback(sm.simple_payback_years)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="text-[10px] text-muted-foreground mt-2">Edit the knobs then Recalculate. Base = your raw inputs.</p>
          </div>

          {r.debt_profile.length > 0 && (
            <div className="card-flat p-4 overflow-x-auto" data-testid="sf-case-debt-profile">
              <div className="flex items-center justify-between mb-2">
                <div className="label-eyebrow">Debt-service profile — {r.label}</div>
                {r.debt_summary && (
                  <div className="text-[11px] text-muted-foreground font-mono">
                    {r.debt_summary.effective_rate}% · {formatSgd(r.debt_summary.monthly_payment)}/mo · interest {formatSgd(r.debt_summary.total_interest)}
                  </div>
                )}
              </div>
              <table className="w-full text-xs">
                <thead><tr className="text-muted-foreground text-left border-b border-border">
                  <th className="py-1.5 pr-2">Year</th><th className="py-1.5 pr-2 text-right">Payment</th>
                  <th className="py-1.5 pr-2 text-right">Interest</th><th className="py-1.5 pr-2 text-right">Principal</th>
                  <th className="py-1.5 text-right">Balance</th>
                </tr></thead>
                <tbody>
                  {r.debt_profile.map((e) => (
                    <tr key={e.year} className="border-b border-border/50">
                      <td className="py-1 pr-2 font-mono">Y{e.year}</td>
                      <td className="py-1 pr-2 text-right font-mono">{formatSgd(e.payment)}</td>
                      <td className="py-1 pr-2 text-right font-mono">{formatSgd(e.interest)}</td>
                      <td className="py-1 pr-2 text-right font-mono">{formatSgd(e.principal)}</td>
                      <td className="py-1 text-right font-mono">{formatSgd(e.balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="text-[10px] text-muted-foreground" data-testid="sf-case-disclaimer">{data.disclaimer}</p>
        </div>
      </div>
    </div>
  );
}
